import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import _ from 'lodash';
import {
    TimeRangeSelector,
    ErrorAlert,
    PadContainer,
    Fields,
    HorizontalFlex,
    timeAndPrecisionTypes,
} from 'smartsuite-ui';
import PrecisionSelector from './PrecisionSelector';
import {
    maxPrecisionValue,
    precisionTypes,
    dateTimeFormat,
} from '../constants/constants';
import { useLabelContext } from '../hooks/labelContext';
import {
    getIsFixedRangeInOrder,
    getIsLastNextRangeValid,
    getIsPrecisionValid,
    isRangeValueWithinMin,
    isRangeValueWithinMax,
    getRangeDomain,
    isFixedRangeWithinMin,
    isFixedRangeWithinMax,
} from './filtersModalUtils';
import {
    getSmallestPrecisionTypeAndPrecisionValue,
    validateTimeAndPrecision,
    isPrecisionValidForRange,
} from './timeAndPrecisionValidation';
import {
    fixedDateIsValidAndDifferent,
    rangeTypeIsDifferent,
    timeAndPrecisionTypeIsDifferent,
} from '../../viewDashboards/utils/configurationAndValidationUtils';

function rangeValueIsDifferent(timeAndPrecision, updatedTimeAndPrecision) {
    return timeAndPrecision.rangeValue !== updatedTimeAndPrecision.rangeValue;
}

// eslint-disable-next-line complexity
function shouldResetPrecisionValues(timeAndPrecision, updatedTimeAndPrecision) {
    const timeAndPrecisionIsDifferent =
        timeAndPrecisionTypeIsDifferent(
            timeAndPrecision,
            updatedTimeAndPrecision
        ) ||
        rangeTypeIsDifferent(timeAndPrecision, updatedTimeAndPrecision) ||
        rangeValueIsDifferent(timeAndPrecision, updatedTimeAndPrecision) ||
        (timeAndPrecision.timeAndPrecisionType === 'fixed' &&
            fixedDateIsValidAndDifferent(
                timeAndPrecision,
                updatedTimeAndPrecision
            ));

    const precisionValidForRange = isPrecisionValidForRange(
        updatedTimeAndPrecision
    );

    return timeAndPrecisionIsDifferent && !precisionValidForRange;
}

/* eslint-disable complexity */
export default function TimeAndPrecisionSelector(props) {
    const { translationLabels, dashboardLabels, interpolateString } =
        useLabelContext();

    const handleTimeRangeChange = (updatedTimeAndPrecision) => {
        let timeAndPrecision = {
            ...updatedTimeAndPrecision,
        };

        if (
            shouldResetPrecisionValues(
                props.timeAndPrecision,
                updatedTimeAndPrecision
            )
        ) {
            const precisionResetValues =
                getSmallestPrecisionTypeAndPrecisionValue(
                    timeAndPrecision,
                    props.precisionTypeOptions
                );

            timeAndPrecision = {
                ...timeAndPrecision,
                ...precisionResetValues,
            };
        }

        if (timeAndPrecision.startDateTime) {
            if (
                timeAndPrecision.timeAndPrecisionType ===
                timeAndPrecisionTypes.fixedDay
            ) {
                timeAndPrecision.startDateTime = moment(
                    timeAndPrecision.startDateTime
                ).startOf('day');

                timeAndPrecision.endDateTime = moment(
                    timeAndPrecision.startDateTime
                )
                    .startOf('day')
                    .add(1, 'days');
            }

            timeAndPrecision.startDateTime = moment(
                timeAndPrecision.startDateTime
            ).format(dateTimeFormat);
        }

        if (timeAndPrecision.endDateTime) {
            timeAndPrecision.endDateTime = moment(
                timeAndPrecision.endDateTime
            ).format(dateTimeFormat);
        }

        if (_.has(timeAndPrecision, 'selectedDateRangeOption')) {
            timeAndPrecision = {
                ...timeAndPrecision,
                ...timeAndPrecision.selectedDateRangeOption,
            };
        }

        props.saveTimeAndPrecision(timeAndPrecision);
    };

    const renderErrorMessage = () => {
        const isFixedRangeInOrder = getIsFixedRangeInOrder(
            props.timeAndPrecision
        );

        if (!isFixedRangeInOrder) {
            return <ErrorAlert>{dashboardLabels.invalidFixedRange}</ErrorAlert>;
        }

        const fixedRangeDomain =
            props.timeAndPrecisionRangeDomains?.fixed ?? {};

        const fixedRangeIsWithinMax = isFixedRangeWithinMax(
            props.timeAndPrecision,
            props.timeAndPrecisionRangeDomains
        );

        const fixedRangeIsWithinMin = isFixedRangeWithinMin(
            props.timeAndPrecision,
            props.timeAndPrecisionRangeDomains
        );

        if (!fixedRangeIsWithinMax) {
            return (
                <ErrorAlert>{`${dashboardLabels.timeRangeExceedsMax} ${
                    fixedRangeDomain.max
                } ${
                    dashboardLabels[fixedRangeDomain.precisionType]
                }`}</ErrorAlert>
            );
        }

        if (!fixedRangeIsWithinMin) {
            return (
                <ErrorAlert>{`${dashboardLabels.timeRangeBelowMin} ${
                    fixedRangeDomain.min
                } ${
                    dashboardLabels[fixedRangeDomain.precisionType]
                }`}</ErrorAlert>
            );
        }

        const isPrecisionValid = getIsPrecisionValid(props.timeAndPrecision);
        const isLastNextRangeValid = getIsLastNextRangeValid(
            props.timeAndPrecision,
            props.timeAndPrecisionRangeDomains
        );

        const rangeDomain = getRangeDomain(
            props.timeAndPrecision,
            props.timeAndPrecisionRangeDomains
        );
        const isLastNextRangeWithinMin = isRangeValueWithinMin(
            props.timeAndPrecision,
            props.timeAndPrecisionRangeDomains
        );
        const isLastNextRangeWithinMax = isRangeValueWithinMax(
            props.timeAndPrecision,
            props.timeAndPrecisionRangeDomains
        );

        if (!isLastNextRangeWithinMin) {
            return (
                <ErrorAlert>{`${dashboardLabels.timeRangeBelowMin} ${
                    rangeDomain.min
                } ${
                    dashboardLabels[props.timeAndPrecision.rangeType]
                }`}</ErrorAlert>
            );
        }

        if (!isLastNextRangeWithinMax) {
            return (
                <ErrorAlert>{`${dashboardLabels.timeRangeExceedsMax} ${
                    rangeDomain.max
                } ${
                    dashboardLabels[props.timeAndPrecision.rangeType]
                }`}</ErrorAlert>
            );
        }

        if (props.timeAndPrecision.precisionValue > maxPrecisionValue) {
            return (
                <ErrorAlert>{`${
                    dashboardLabels.timeRangeExceedsMax
                } ${maxPrecisionValue} ${
                    dashboardLabels[props.timeAndPrecision.precisionType]
                }`}</ErrorAlert>
            );
        }

        if (!isLastNextRangeValid || !isPrecisionValid) {
            return null;
        }

        const { isTimeAndPrecisionValid, errors } = validateTimeAndPrecision(
            props.timeAndPrecision,
            interpolateString,
            dashboardLabels,
            props.isGauge
        );

        if (isTimeAndPrecisionValid) {
            return null;
        }

        return (
            <ErrorAlert>
                <ul>
                    {errors.map((error) => (
                        <li key={error}>{error}</li>
                    ))}
                </ul>
            </ErrorAlert>
        );
    };

    if (!props.showTimeAndPrecision) {
        return null;
    }

    let timeAndPrecision = { ...props.timeAndPrecision };
    if (timeAndPrecision.startDateTime) {
        timeAndPrecision = {
            ...timeAndPrecision,
            startDateTime: moment(timeAndPrecision.startDateTime).toDate(),
        };
    }
    if (timeAndPrecision.endDateTime) {
        timeAndPrecision = {
            ...timeAndPrecision,
            endDateTime: moment(timeAndPrecision.endDateTime).toDate(),
        };
    }

    return (
        <>
            <PadContainer
                cssClassModifiers={['pad-medium', 'no-pad-left-right']}
            >
                {renderErrorMessage()}
                <HorizontalFlex
                    cssClassModifiers={['flex-wrap', 'align-flex-end']}
                >
                    <Fields
                        cssClassModifiers={[
                            'no-background',
                            'no-pad-top-bottom',
                        ]}
                    >
                        <TimeRangeSelector
                            disabled={props.disabled}
                            labels={{
                                ...translationLabels,
                                ...dashboardLabels,
                            }}
                            precisionTypeOptions={
                                props.precisionTypeOptions
                                    ? _.keys(props.precisionTypeOptions)
                                    : _.values(precisionTypes)
                            }
                            predefinedDateRangeOptions={
                                props.predefinedDateRanges
                            }
                            predicateLabel={dashboardLabels.timeRange}
                            saveTimeAndPrecision={handleTimeRangeChange}
                            timeAndPrecision={timeAndPrecision}
                            timeAndPrecisionOptions={
                                props.timeAndPrecisionTypeOptions
                            }
                            useTimeRangeShifter={
                                props.timeAndPrecision.useTimeRangeShifter
                            }
                        />
                    </Fields>
                    <PrecisionSelector
                        disabled={props.disabled}
                        precisionTypeOptions={
                            // prettier-ignore
                            props.precisionTypeOptions
                                ? props.precisionTypeOptions[
                                    timeAndPrecision.rangeType
                                ] ?? _.values(precisionTypes)
                                : _.values(precisionTypes)
                        }
                        saveTimeAndPrecision={handleTimeRangeChange}
                        showPrecisionConfig={props.showPrecisionConfig}
                        timeAndPrecision={timeAndPrecision}
                        isGauge={props.isGauge}
                    />
                </HorizontalFlex>
            </PadContainer>
            <hr className="divider divider--criteria" />
        </>
    );
}
/* eslint-enable complexity */

TimeAndPrecisionSelector.propTypes = {
    isGauge: PropTypes.bool.isRequired,
    precisionTypeOptions: PropTypes.instanceOf(Object),
    predefinedDateRanges: PropTypes.instanceOf(Array).isRequired,
    showPrecisionConfig: PropTypes.bool.isRequired,
    showTimeAndPrecision: PropTypes.bool.isRequired,
    timeAndPrecision: PropTypes.instanceOf(Object).isRequired,
    disabled: PropTypes.bool,
    saveTimeAndPrecision: PropTypes.func,
    timeAndPrecisionTypeOptions: PropTypes.instanceOf(Array),
    timeAndPrecisionRangeDomains: PropTypes.instanceOf(Object),
};
